import { useMediaQuery, useTheme } from '@mui/material';
import {
  useGridApiRef,
  type GridColumnResizeParams,
  type GridColumnVisibilityModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { useAppContext } from '~/contexts';

const DeviceGridStateSchema = z.object({
  columnOrder: z.array(z.string()).optional(),
  columnVisibility: z.record(z.boolean()).optional(),
  columnWidths: z.record(z.number()).optional(),
});

type DeviceGridState = z.infer<typeof DeviceGridStateSchema>;

const STORAGE_KEY = 'devices:list';

const getStoredState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const result = DeviceGridStateSchema.safeParse(JSON.parse(stored));
    if (!result.success) {
      console.error('Invalid stored device grid state:', result.error);
      return null;
    }
    return result.data;
  } catch (error) {
    console.error('Failed to get stored device grid state:', error);
    return null;
  }
};

const setStoredState = (state: Partial<DeviceGridState>) => {
  try {
    const existing = getStoredState() ?? {};
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...existing, ...state }));
  } catch (error) {
    console.error('Failed to store device grid state:', error);
  }
};

const clearStoredState = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear device grid state:', error);
  }
};

export const useDeviceGridState = () => {
  const apiRef = useGridApiRef();
  const [initialState] = useState(getStoredState);
  const hasPersistedPreferences = useRef(initialState !== null);
  const hasPersistedVisibility = useRef(initialState?.columnVisibility != null);

  const [columnOrder, setColumnOrder] = useState(() => initialState?.columnOrder ?? []);
  const [columnVisibility, setColumnVisibility] = useState(initialState?.columnVisibility ?? {});
  const [columnWidths, setColumnWidths] = useState(initialState?.columnWidths ?? {});

  const { currentUser } = useAppContext();
  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeAndDown = useMediaQuery(theme.breakpoints.down('lg'));
  const isXLargeAndDown = useMediaQuery(theme.breakpoints.down('xl'));
  const isXXLargeAndDown = useMediaQuery('(max-width:2400px)');

  const responsiveVisibility = useCallback(
    (): GridColumnVisibilityModel => ({
      Indicators: !isSmallAndDown,
      activeShow: !isLargeAndDown,
      arch: !isXXLargeAndDown && currentUser.admin,
      channels: !isXXLargeAndDown,
      connected: !isXLargeAndDown,
      cpuPercentage: false,
      cpuTemperature: false,
      diskUsage: false,
      displayServer: false,
      group: !isLargeAndDown,
      internal: !isXXLargeAndDown && currentUser.admin,
      ipAddress: !isXLargeAndDown,
      kind: !isXXLargeAndDown && currentUser.admin,
      memoryUsage: false,
      model: !isXLargeAndDown,
      osUptime: !isXXLargeAndDown && currentUser.admin,
      playerVersion: !isXLargeAndDown,
      processUptime: !isXXLargeAndDown && currentUser.admin,
    }),
    [currentUser, isLargeAndDown, isSmallAndDown, isXLargeAndDown, isXXLargeAndDown],
  );

  useEffect(() => {
    if (hasPersistedVisibility.current) return;
    setColumnVisibility((x) => ({ ...x, ...responsiveVisibility() }));
  }, [responsiveVisibility]);

  const onColumnVisibilityChange = useCallback((model: GridColumnVisibilityModel) => {
    hasPersistedPreferences.current = true;
    hasPersistedVisibility.current = true;
    setColumnVisibility(model);
    setStoredState({ columnVisibility: model });
  }, []);

  const onColumnWidthChange = useCallback((params: GridColumnResizeParams) => {
    hasPersistedPreferences.current = true;
    setColumnWidths((prev) => {
      const newWidths = { ...prev, [params.colDef.field]: params.width };
      setStoredState({ columnWidths: newWidths });
      return newWidths;
    });
  }, []);

  const onColumnOrderChange = useCallback(() => {
    hasPersistedPreferences.current = true;
    const orderedFields = apiRef.current.getAllColumns().map((col) => col.field);
    setColumnOrder(orderedFields);
    setStoredState({ columnOrder: orderedFields });
  }, [apiRef]);

  const resetPreferences = useCallback(() => {
    hasPersistedPreferences.current = false;
    hasPersistedVisibility.current = false;
    clearStoredState();
    setColumnOrder([]);
    setColumnVisibility(responsiveVisibility());
    setColumnWidths({});
  }, [responsiveVisibility]);

  return {
    apiRef,
    columnOrder,
    columnVisibility,
    columnWidths,
    hasPersistedPreferences,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onColumnWidthChange,
    resetPreferences,
  };
};
